import { module as modujs } from "modujs";
import { gsap } from "gsap";

export default class extends modujs {
	constructor(m) {
		super(m);
		this.events = {
			click: { head: "onClick" },
			keydown: { head: "onKeydown" },
		};
	}

	init() {
		this.$head = this.$("head")[0];
		this.$body = this.$("body")[0];
		this.$content = this.$("content")[0];

		if (!this.$head || !this.$body || !this.$content) {
			console.warn("[Accordion] Missing parts", { head: !!this.$head, body: !!this.$body, content: !!this.$content });
			return;
		}

		// Initial collapsed state
		this.$head.setAttribute("aria-expanded", "false");
		this.$body.setAttribute("aria-expanded", "false");
		this.$body.hidden = true;
		this.$body.style.overflow = "hidden";
		this.$body.style.height = "0px";

		// Measurements (after fonts) + on resize
		(document.fonts?.ready || Promise.resolve()).then(() => this.calc());
		this._resizeHandler = this.resize.bind(this);
		window.addEventListener("resize", this._resizeHandler);
	}

	calc() {
		const h = this.$content?.getBoundingClientRect().height || 0;
		this.el.style.setProperty("--accordion-open-height", `${h}px`);
	}

	resize() {
		if (this.resizeTimeout) this.resizeTimeout.kill?.();
		this.el.classList.add("is-resizing");
		this.calc();
		this.resizeTimeout = gsap.delayedCall(0.1, () => {
			this.el.classList.remove("is-resizing");
		});
	}

	open() {
		// Make visible first so transition can run
		this.$body.hidden = false;

		// ARIA
		this.$head.setAttribute("aria-expanded", "true");
		this.$body.setAttribute("aria-expanded", "true");

		// Measure & animate 0 -> content height
		const h = this.$content.getBoundingClientRect().height || 0;
		this.$body.style.height = "0px";
		// force reflow

		this.$body.offsetHeight;
		this.$body.style.height = `${h}px`;

		const onEnd = (e) => {
			if (e.target !== this.$body) return;
			this.$body.style.height = "auto";
			this.$body.removeEventListener("transitionend", onEnd);
		};
		this.$body.addEventListener("transitionend", onEnd);

		this.el.classList.add("is-expanded");
		this.calc(); // optional recalc in case content changed
	}

	close() {
		// Animate current height -> 0
		const current = this.$body.getBoundingClientRect().height || 0;
		this.$body.style.height = `${current}px`;
		// force reflow

		this.$body.offsetHeight;
		this.$body.style.height = "0px";

		// ARIA
		this.$head.setAttribute("aria-expanded", "false");
		this.$body.setAttribute("aria-expanded", "false");

		const onEnd = (e) => {
			if (e.target !== this.$body) return;
			this.$body.hidden = true; // hide only AFTER the collapse animation
			this.$body.style.height = "0px"; // keep a clean baseline
			this.$body.removeEventListener("transitionend", onEnd);
		};
		this.$body.addEventListener("transitionend", onEnd);

		this.el.classList.remove("is-expanded");
	}

	toggle(expandTo) {
		const isExpanded = this.$head.getAttribute("aria-expanded") === "true";
		const next = typeof expandTo === "boolean" ? expandTo : !isExpanded;
		next ? this.open() : this.close();
	}

	onClick() {
		this.toggle();
	}

	onKeydown(e) {
		// Space/Enter toggles
		if (e.key === " " || e.key === "Enter") {
			e.preventDefault();
			this.toggle();
		}
		// Escape collapses
		if (e.key === "Escape") this.toggle(false);
	}

	destroy() {
		window.removeEventListener("resize", this._resizeHandler);
		if (this.resizeTimeout) this.resizeTimeout.kill?.();
	}
}
